import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '@/contexts/UserContext';
import { useStats } from '@/contexts/StatsContext';
import {
  Clock,
  Heart,
  Baby,
  User,
  RefreshCw,
  Zap,
  AlertCircle,
  X,
  Crown,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { trackSuggestionAction } from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const ActionDetailPanel = ({ isOpen, onClose, suggestion }) => {
  const { t, i18n } = useTranslation(['translation', 'common']);
  const { user, isPremium } = useUser();
  const { refreshStats } = useStats();

  const userId = user?.user_id || user?.id;

  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [requestingNew, setRequestingNew] = useState(false);
  const [remainingRequests, setRemainingRequests] = useState(5);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const triggerConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    // Create explicit canvas with transparent background to avoid black square on Android WebView
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:9999;background:transparent;';
    document.body.appendChild(canvas);
    const fire = confetti.create(canvas, { resize: true });

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) { clearInterval(interval); setTimeout(() => canvas.remove(), 500); return; }

      const particleCount = 50 * (timeLeft / duration);
      fire({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      fire({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);
  };

  const handleFeedback = async (status) => {
    if (!suggestion) return;

    if (status === 'done') {
      setShowNotes(true);
      return;
    }

    try {
      await axios.post(`${API}/suggestion/feedback`, { suggestion_id: suggestion.suggestion_id, status });
      toast.success(t('dashboard.feedbackSuccess'));
      suggestion.status = status;
      // KPI tracking
      trackSuggestionAction(userId, {
        suggestionId: suggestion.suggestion_id,
        action: 'SKIP',
        category: suggestion.main_suggestion?.category,
      });
      refreshStats();
      onClose();
    } catch (e) {
      console.error('[ActionDetailPanel] feedback error:', e.response?.data || e);
      toast.error(i18n.language === 'it' ? 'Errore nel salvataggio del feedback' : i18n.language === 'es' ? 'Error al guardar el feedback' : 'Error saving feedback');
    }
  };

  const handleSaveWithNotes = async () => {
    if (!suggestion) return;

    try {
      await axios.post(
        `${API}/suggestion/feedback`,
        {
          suggestion_id: suggestion.suggestion_id,
          status: 'done',
          notes: notes.trim() || null
        },
        []
      );

      triggerConfetti();
      toast.success(getRandomEncouragement(), { duration: 4000 });

      suggestion.status = 'done';
      suggestion.notes = notes.trim();
      // KPI tracking
      trackSuggestionAction(userId, {
        suggestionId: suggestion.suggestion_id,
        action: 'DONE',
        category: suggestion.main_suggestion?.category,
      });
      setShowNotes(false);
      setNotes('');
      refreshStats();
      onClose();
    } catch (e) {
      console.error('[ActionDetailPanel] save notes error:', e.response?.data || e);
      toast.error(i18n.language === 'it' ? 'Errore nel salvataggio del feedback' : i18n.language === 'es' ? 'Error al guardar el feedback' : 'Error saving feedback');
    }
  };

  const requestNewSuggestion = useCallback(async () => {
    if (!userId || !isPremium) return;
    try {
      setRequestingNew(true);
      const res = await axios.post(`${API}/suggestion/generate-new/${userId}`);
      const newSuggestion = { ...res.data, status: 'pending' };
      if (res.data.remaining_requests !== undefined) {
        setRemainingRequests(res.data.remaining_requests);
      }
      onClose();
      // Navigate to dashboard or refresh
      window.location.reload(); // Simple refresh for now
    } catch (e) {
      console.error('[ActionDetailPanel] requestNewSuggestion error:', e.response?.data || e);
      const errorMsg =
        e.response?.data?.detail ||
        (i18n.language === 'it' ? 'Errore nella generazione' : i18n.language === 'es' ? 'Error en la generación' : 'Generation error');
      toast.error(errorMsg);
    } finally {
      setRequestingNew(false);
    }
  }, [userId, isPremium, onClose, i18n.language]);

  const getRandomEncouragement = () => {
    const messages = t('dashboard.congratulations', { returnObjects: true });
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'husband':
        return <Heart className="w-5 h-5" />;
      case 'father':
        return <Baby className="w-5 h-5" />;
      case 'partner':
        return <User className="w-5 h-5" />;
      default:
        return <Heart className="w-5 h-5" />;
    }
  };

  const getContextLabel = (context) => {
    const contextMap = {
      morning: i18n.language === 'it' ? (<><span role="img" aria-label="Mattina">🌅</span> Mattina</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Mañana">🌅</span> Mañana</>) : (<><span role="img" aria-label="Morning">🌅</span> Morning</>),
      afternoon: i18n.language === 'it' ? (<><span role="img" aria-label="Pomeriggio">☀️</span> Pomeriggio</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Tarde">☀️</span> Tarde</>) : (<><span role="img" aria-label="Afternoon">☀️</span> Afternoon</>),
      evening: i18n.language === 'it' ? (<><span role="img" aria-label="Sera">🌙</span> Sera</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Noche">🌙</span> Noche</>) : (<><span role="img" aria-label="Evening">🌙</span> Evening</>),
      anytime: i18n.language === 'it' ? (<><Clock className="inline w-4 h-4 mr-1 align-text-bottom" />Quando vuoi</>) : i18n.language === 'es' ? (<><Clock className="inline w-4 h-4 mr-1 align-text-bottom" />Cuando quieras</>) : (<><Clock className="inline w-4 h-4 mr-1 align-text-bottom" />Anytime</>),
      weekend: i18n.language === 'it' ? (<><span role="img" aria-label="Weekend">🛋️</span> Nel weekend</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Fin de semana">🛋️</span> El fin de semana</>) : (<><span role="img" aria-label="Weekend">🛋️</span> On the weekend</>),
    };
    return contextMap[context] || String(context || '').replace(/_/g, ' ');
  };

  const getTimeIndicator = (context) => {
    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
    if (context === 'anytime') return null;
    return context === timeOfDay;
  };

  const renderDescription = (description) => {
    if (!description) return null;

    // Split description into lines
    const lines = description.split('\n').filter(line => line.trim());

    // If description has 3 or fewer lines, show all
    if (lines.length <= 3) {
      return (
        <p className="text-base leading-relaxed text-stone-700" style={{ fontFamily: 'Public Sans, sans-serif' }}>
          {description}
        </p>
      );
    }

    // Show truncated version or full version based on expanded state
    if (!descriptionExpanded) {
      return (
        <div className="relative">
          <div className="text-base leading-relaxed text-stone-700" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            {/* First line - normal */}
            <div className="mb-1">{lines[0]}</div>
            {/* Second line - faded */}
            <div className="mb-1 blur-sm opacity-60">{lines[1]}</div>
            {/* Third line - faded */}
            <div className="mb-1 blur-sm opacity-60">{lines[2]}</div>
          </div>
          {/* Clickable overlay for expansion */}
          <div
            className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent cursor-pointer flex items-end justify-center pb-1"
            onClick={() => setDescriptionExpanded(true)}
          >
            <span className="text-stone-500 text-sm font-medium">...</span>
          </div>
        </div>
      );
    } else {
      // Show full description with collapse option
      return (
        <div>
          <p className="text-base leading-relaxed text-stone-700 mb-2" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            {description}
          </p>
          <button
            onClick={() => setDescriptionExpanded(false)}
            className="text-stone-500 text-sm hover:text-stone-700 transition-colors"
          >
            {i18n.language === 'it' ? 'Mostra meno' : i18n.language === 'es' ? 'Mostrar menos' : 'Show less'}
          </button>
        </div>
      );
    }
  };

  if (!suggestion) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[100]"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-xl border border-stone-200 z-[101]"
            style={{
              maxHeight: '95vh',
              paddingBottom: 'max(env(safe-area-inset-bottom), 20px)'
            }}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-stone-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between relative">
              <h2 className="font-serif text-lg text-stone-600 absolute left-1/2 transform -translate-x-1/2 m-0 p-0 w-max">
                {i18n.language === 'it' ? 'Focus' : i18n.language === 'es' ? 'Enfoque' : 'Focus'}
              </h2>
              <div className="flex-1" />
              <button
                onClick={onClose}
                className="p-2 -mr-2 text-stone-400 hover:text-stone-600 transition-colors ml-auto z-10"
                aria-label={i18n.language === 'it' ? 'Chiudi' : i18n.language === 'es' ? 'Cerrar' : 'Close'}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-6 py-6" style={{ maxHeight: 'calc(90vh - 140px)' }}>

              {/* Description */}
              {suggestion.main_suggestion.description && (
                <div className="mb-6">
                  {renderDescription(suggestion.main_suggestion.description)}
                </div>
              )}

              {/* Metadata */}
              {/* Metadata removed as requested */}

              {/* Tension Addressed */}
              {suggestion.main_suggestion.addresses_tension && (
                <p className="text-sm text-stone-500 mb-6">
                  {i18n.language === 'it' ? 'Utile quando la giornata è stata pesante.' : i18n.language === 'es' ? 'Útil cuando el día ha sido pesado.' : 'Useful when the day has been tough.'}
                </p>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {suggestion.status !== 'done' ? (
                  <>
                    <button
                      onClick={() => handleFeedback('done')}
                      className="btn-hide-icon w-full h-14 bg-stone-900 text-white rounded-full font-semibold hover:bg-stone-800 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      {i18n.language === 'it' ? 'Fatto!' : i18n.language === 'es' ? '¡Hecho!' : 'Done!'}
                    </button>

                    <button
                      onClick={() => handleFeedback('postponed')}
                      className="w-full h-12 bg-inherit text-[#a8a29e] rounded-full font-medium hover:text-stone-700 transition-colors underline border-none"
                    >
                      {i18n.language === 'it' ? 'Rimanda' : i18n.language === 'es' ? 'Posponer' : 'Postpone'}
                    </button>
                  </>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
                    <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="font-medium text-green-900">
                      {i18n.language === 'it' ? 'Completato!' : i18n.language === 'es' ? '¡Completado!' : 'Completed!'}
                    </p>
                    {suggestion.notes && (
                      <p className="text-sm text-green-700 mt-2">{suggestion.notes}</p>
                    )}
                  </div>
                )}

                {!isPremium && (
                  <button
                    onClick={() => handleFeedback('done')} // Placeholder for paywall
                    className="btn-hide-icon w-full h-12 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-full font-medium text-amber-700 flex items-center justify-center gap-2"
                  >
                    <Crown className="w-4 h-4" />
                    {i18n.language === 'it' ? 'Prova Premium' : i18n.language === 'es' ? 'Probar Premium' : 'Try Premium'}
                  </button>
                )}
              </div>
            </div>

            {/* Notes Modal */}
            {showNotes && (
              <div className="fixed inset-0 z-[102] flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95">
                  <div>
                    <h3 className="font-serif text-xl text-stone-900 mb-2">
                      {i18n.language === 'it' ? 'Note (opzionale)' : i18n.language === 'es' ? 'Notas (opcional)' : 'Notes (optional)'}
                    </h3>
                    <p className="text-sm text-stone-500">
                      {i18n.language === 'it' ? 'Aggiungi note su come è andata' : i18n.language === 'es' ? 'Agrega notas sobre cómo te fue' : 'Add notes about how it went'}
                    </p>
                  </div>

                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={i18n.language === 'it' ? 'Scrivi una nota...' : i18n.language === 'es' ? 'Escribe una nota...' : 'Write a note...'}
                    className="w-full h-32 p-4 border border-stone-200 rounded-full focus:border-stone-900 focus:ring-0 resize-none"
                  />

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowNotes(false);
                        setNotes('');
                      }}
                      className="flex-1 h-12 rounded-full border border-stone-200 text-stone-900 hover:bg-stone-50 transition-all"
                    >
                      {i18n.language === 'it' ? 'Annulla' : i18n.language === 'es' ? 'Cancelar' : 'Cancel'}
                    </button>

                    <button
                      onClick={handleSaveWithNotes}
                      className="flex-1 h-12 rounded-full bg-stone-900 text-white hover:bg-stone-800 transition-all"
                    >
                      {i18n.language === 'it' ? 'Salva' : i18n.language === 'es' ? 'Guardar' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

function mapDifficulty(diff, language) {
  switch (diff) {
    case 'low':
    case 'Facile':
      return language === 'it' ? 'Facile' : 'Easy';
    case 'medium':
    case 'Medio':
      return language === 'it' ? 'Medio' : 'Medium';
    case 'high':
    case 'Difficile':
      return language === 'it' ? 'Difficile' : 'Hard';
    default:
      return diff;
  }
}

function mapCategory(cat, language) {
  switch (cat) {
    case 'husband':
    case 'Marito':
      return language === 'it' ? 'Marito' : language === 'es' ? 'Esposo' : 'Husband';
    case 'father':
    case 'Padre':
      return language === 'it' ? 'Padre' : language === 'es' ? 'Padre' : 'Father';
    case 'partner':
    case 'Partner':
      return language === 'it' ? 'Partner' : language === 'es' ? 'Pareja' : 'Partner';
    default:
      return cat;
  }
}

export default ActionDetailPanel;